import { __esDecorate, __runInitializers } from "tslib";
import { Logger } from '#library/logger.class.js';
import { markConfig } from '#library/symbol.library.js';
import { asArray } from '#library/coercion.library.js';
import { ifDefined } from '#library/object.library.js';
import { secure } from '#library/proxy.library.js';
import { cleanify } from '#library/json.library.js';
import { Immutable } from '#library/class.library.js';
import { isEmpty, isObject } from '#library/assertion.library.js';
const _dbg = new Logger('[Pledge]');
let _static = {};
const _STATE = secure({
    Pending: Symbol('pending'),
    Resolved: Symbol('resolved'),
    Rejected: Symbol('rejected')
});
/**
 * A lightweight wrapper around Promises (utilizing `Promise.withResolvers`)
 * that exposes `resolve`, `reject`, and state-tracking methods.
 * Useful for deferred execution and managing asynchronous lifecycles.
 *
 * @example
 * ```ts
 * const p = new Pledge`<string>`('MyPledge');
 * p.resolve('Success!');
 * await p.promise; // 'Success!'
 * ```
 */
let Pledge = (() => {
    let _classDecorators = [Immutable];
    let _classDescriptor;
    let _classExtraInitializers = [];
    let _classThis;
    var Pledge = class {
        static { _classThis = this; }
        static {
            const _metadata = typeof Symbol === "function" && Symbol.metadata ? Object.create(null) : void 0;
            __esDecorate(null, _classDescriptor = { value: _classThis }, _classDecorators, { kind: "class", name: _classThis.name, metadata: _metadata }, null, _classExtraInitializers);
            Pledge = _classThis = _classDescriptor.value;
            if (_metadata) Object.defineProperty(_classThis, Symbol.metadata, { enumerable: true, configurable: true, writable: true, value: _metadata });
            __runInitializers(_classThis, _classExtraInitializers);
        }
        #pledge;
        #status = {};
        static get STATE() { return _STATE; }
        /**
         * Initializes global defaults for all future Pledge instances.
         *
         * @param arg - The global configuration or tag
         * @returns The updated static status configuration
         */
        static init(arg) {
            if (isObject(arg)) {
                if (isEmpty(arg))
                    _static = {}; // reset static values
                markConfig(_static);
                Object.assign(_static, ifDefined({ tag: arg.tag, debug: arg.debug, catch: arg.catch, silent: arg.silent }), ifDefined({ onResolve: arg.onResolve, onReject: arg.onReject, onSettle: arg.onSettle, }));
            }
            else {
                markConfig(_static);
                Object.assign(_static, ifDefined({ tag: arg, }));
            }
            _dbg.debug(_static, _static);
            return Pledge.status;
        }
        /** reset static defaults */
        static [Symbol.dispose]() { Pledge.init({}); }
        static get status() {
            return { ..._static, state: _STATE.Pending };
        }
        /**
         * Creates a new Pledge instance.
         *
         * @param arg - An optional configuration object or string tag
         */
        constructor(arg) {
            const opts = isObject(arg) ? arg : { tag: arg };
            const config = { ..._static, ...ifDefined({ tag: opts.tag, debug: opts.debug, catch: opts.catch, silent: opts.silent }) };
            if (Promise.withResolvers) {
                this.#pledge = Promise.withResolvers();
            }
            else {
                let res, rej;
                const promise = new Promise((resolve, reject) => {
                    res = resolve;
                    rej = reject;
                });
                this.#pledge = { promise, resolve: res, reject: rej };
            }
            this.#status = markConfig({ state: _STATE.Pending, ...config });
            const onResolve = asArray(_static.onResolve).concat(asArray(opts.onResolve));
            const onReject = asArray(_static.onReject).concat(asArray(opts.onReject));
            const onSettle = asArray(_static.onSettle).concat(asArray(opts.onSettle));
            const runSafely = (callbacks, ...args) => {
                callbacks.forEach(cb => {
                    try {
                        cb(...args);
                    }
                    catch (err) {
                        _dbg.warn(this.#status, 'Pledge callback failed', err);
                    }
                });
            };
            if (onResolve.length)
                this.#pledge.promise.then(val => runSafely(onResolve, val));
            if (onReject.length)
                this.#pledge.promise.catch(err => runSafely(onReject, err));
            if (onSettle.length)
                this.#pledge.promise.finally(() => runSafely(onSettle));
            if (this.#status.catch)
                this.#pledge.promise.catch(err => _dbg.warn(this.#status, err));
            return Object.freeze(this);
        }
        get [Symbol.toStringTag]() {
            return 'Pledge';
        }
        [Symbol.dispose]() {
            if (this.isPending) {
                try {
                    this.promise.catch(() => { });
                }
                catch {
                    // best-effort; preserve disposal semantics even if promise is unavailable
                }
                this.reject(new Error(`Pledge disposed`)); // dispose
            }
        }
        get status() {
            return cleanify(this.#status);
        }
        get promise() {
            return this.#pledge.promise;
        }
        get state() {
            return this.#status.state.description;
        }
        get isPending() {
            return this.#status.state === _STATE.Pending;
        }
        get isResolved() {
            return this.#status.state === _STATE.Resolved;
        }
        get isRejected() {
            return this.#status.state === _STATE.Rejected;
        }
        get isSettled() {
            return this.#status.state !== _STATE.Pending;
        }
        toString() {
            return JSON.stringify(this.status);
        }
        /**
         * Resolves the underlying promise.
         *
         * @param value - The value to resolve the promise with
         * @returns The internal promise
         */
        resolve(value) {
            if (this.isPending) {
                this.#status.settled = value;
                this.#status.state = _STATE.Resolved;
                _dbg.debug(this.#status, 'Resolved'); // debug
                this.#pledge.resolve(value); // resolve
            }
            // else _dbg.warn(this.#status, `Pledge was already ${this.state}`);
            return this.#pledge.promise;
        }
        /**
         * Rejects the underlying promise.
         *
         * @param error - The reason for rejection
         * @returns The internal promise
         */
        reject(error) {
            if (this.isPending) {
                this.#status.error = error;
                this.#status.state = _STATE.Rejected;
                _dbg.debug(this.#status, 'Rejected', error); // debug
                this.#pledge.reject(error); // reject
            }
            // else _dbg.warn(this.#status, `Pledge was already ${this.state}`);
            return this.#pledge.promise;
        }
        /**
         * Makes the Pledge 'then-able', allowing it to be directly awaited
         * or chained like a standard Promise.
         *
         * @param onfulfilled - Callback for when the pledge resolves
         * @param onrejected - Callback for when the pledge rejects
         * @returns A new promise representing the chained execution
         */
        then(onfulfilled, onrejected) {
            return this.promise.then(onfulfilled, onrejected);
        }
    };
    return Pledge = _classThis;
})();
export { Pledge };
